import { createSlice, PayloadAction } from '@reduxjs/toolkit';

interface UIState {
    theme: 'light' | 'dark';
    sidebarOpen: boolean;
    activeTab: string;
    loading: boolean;
    notifications: Notification[];
    modal: {
        open: boolean;
        type: string | null;
        data: any;
    };
}

interface Notification {
    id: string;
    type: 'info' | 'success' | 'warning' | 'error';
    title: string;
    message: string;
    timestamp: number;
    autoHide?: boolean;
    duration?: number;
}

const initialState: UIState = {
    theme: 'dark',
    sidebarOpen: true,
    activeTab: 'dashboard',
    loading: false,
    notifications: [],
    modal: {
        open: false,
        type: null,
        data: null,
    },
};

const uiSlice = createSlice({
    name: 'ui',
    initialState,
    reducers: {
        setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
            state.theme = action.payload;
        },
        toggleSidebar: (state) => {
            state.sidebarOpen = !state.sidebarOpen;
        },
        setSidebarOpen: (state, action: PayloadAction<boolean>) => {
            state.sidebarOpen = action.payload;
        },
        setActiveTab: (state, action: PayloadAction<string>) => {
            state.activeTab = action.payload;
        },
        setLoading: (state, action: PayloadAction<boolean>) => {
            state.loading = action.payload;
        },
        addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
            const notification: Notification = {
                ...action.payload,
                id: crypto.randomUUID(),
                timestamp: Date.now(),
            };
            state.notifications.push(notification);
        },
        removeNotification: (state, action: PayloadAction<string>) => {
            state.notifications = state.notifications.filter(n => n.id !== action.payload);
        },
        clearNotifications: (state) => {
            state.notifications = [];
        },
        openModal: (state, action: PayloadAction<{ type: string; data?: any }>) => {
            state.modal = {
                open: true,
                type: action.payload.type,
                data: action.payload.data || null,
            };
        },
        closeModal: (state) => {
            state.modal = {
                open: false,
                type: null,
                data: null,
            };
        },
    },
});

export const {
    setTheme,
    toggleSidebar,
    setSidebarOpen,
    setActiveTab,
    setLoading,
    addNotification,
    removeNotification,
    clearNotifications,
    openModal,
    closeModal,
} = uiSlice.actions;

export default uiSlice.reducer;